'use client';

import { useEffect, useRef, useState } from 'react';
import {
  trackBudgetCalculatorCalculate,
  trackBudgetCalculatorEditAmounts,
  trackBudgetCalculatorReset,
} from 'components/analytics/eventHandlerTracking';
import { calculateBudget } from './budget';
import { BudgetCalculatorForm } from './BudgetCalculatorForm';
import { BudgetCalculatorResult } from './BudgetCalculatorResult';
import { BudgetCalculatorSummary } from './BudgetCalculatorSummary';
import {
  BUDGET_FIELDS,
  DISCLAIMER,
  INCOME_ERROR,
  INCOME_FIELD,
  INITIAL_VALUES,
  MAX_FIELD_VALUE,
  RANGE_ERROR,
} from './constants';
import { formatCurrency, parseCurrency, sanitizeCurrencyInput } from './currency';
import type { BudgetFieldErrors, BudgetFieldValues, BudgetState } from './types';

const INITIAL_STATE: BudgetState = { values: INITIAL_VALUES, errors: {}, result: null };

const validateValues = (values: BudgetFieldValues): BudgetFieldErrors => {
  const errors: BudgetFieldErrors = {};

  // Sanitization strips negative signs, so only the ceiling needs checking.
  BUDGET_FIELDS.forEach(({ label }) => {
    if (parseCurrency(values[label]) > MAX_FIELD_VALUE) errors[label] = RANGE_ERROR;
  });

  const incomeInCents = Math.round(parseCurrency(values[INCOME_FIELD.label]) * 100);

  if (incomeInCents <= 0) errors[INCOME_FIELD.label] = INCOME_ERROR;

  return errors;
};

/** Defers tracking so click work stays off INP. */
const deferTracking = (track: () => void) => setTimeout(track, 0);

export const BudgetCalculator = () => {
  const [state, setState] = useState<BudgetState>(INITIAL_STATE);
  const summaryRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  /** Prevents the initial render from claiming focus. */
  const panelSwapped = useRef(false);

  // A panel swap unmounts its triggering button and drops focus to <body>.
  useEffect(() => {
    if (!panelSwapped.current) return;

    panelSwapped.current = false;
    (state.result === null ? formRef.current : summaryRef.current)?.focus();
  }, [state.result]);

  const updateValue = (label: string, transform: (raw: string) => string) =>
    setState(previous => ({
      ...previous,
      values: { ...previous.values, [label]: transform(previous.values[label]) },
    }));

  const handleFieldChange = (label: string, value: string) => updateValue(label, () => sanitizeCurrencyInput(value));

  // Editing uses unformatted digits.
  const handleFieldFocus = (label: string) => updateValue(label, raw => (raw === '' ? '' : String(parseCurrency(raw))));

  const handleFieldBlur = (label: string) =>
    updateValue(label, raw => (raw === '' ? '' : formatCurrency(parseCurrency(raw))));

  const handleCalculate = () => {
    const errors = validateValues(state.values);
    const hasErrors = Object.keys(errors).length > 0;
    const result = hasErrors ? null : calculateBudget(state.values, parseCurrency(state.values[INCOME_FIELD.label]));

    setState(previous => ({ ...previous, errors, result }));

    deferTracking(() => {
      trackBudgetCalculatorCalculate();
    });

    if (result === null) return;

    panelSwapped.current = true;
  };

  /** Report which clear action ran before both paths share initial state. */
  const handleClear = (label: 'Reset' | 'Start Over') => {
    // Only Start over replaces a panel.
    panelSwapped.current = state.result !== null;

    setState(INITIAL_STATE);

    deferTracking(() => {
      trackBudgetCalculatorReset({ label });
    });
  };

  const handleEdit = () => {
    panelSwapped.current = true;

    setState(previous => ({ ...previous, result: null }));

    deferTracking(() => {
      trackBudgetCalculatorEditAmounts();
    });
  };

  return (
    <div className="rounded-xl bg-bg-raised-secondary p-6 font-sans sm:p-10">
      {/* The page provides the h1. */}
      <h2 className="mb-6 text-heading-md">Budget Calculator</h2>

      {/* Keep bar details out of the live summary. */}
      <div role="status" ref={summaryRef} tabIndex={-1}>
        {state.result !== null && <BudgetCalculatorSummary result={state.result} />}
      </div>

      {state.result === null ? (
        <BudgetCalculatorForm
          ref={formRef}
          values={state.values}
          errors={state.errors}
          onFieldChange={handleFieldChange}
          onFieldFocus={handleFieldFocus}
          onFieldBlur={handleFieldBlur}
          onCalculate={handleCalculate}
          onReset={() => handleClear('Reset')}
        />
      ) : (
        <BudgetCalculatorResult
          result={state.result}
          onEdit={handleEdit}
          onStartOver={() => handleClear('Start Over')}
        />
      )}

      <p className="mt-8 text-legal">{DISCLAIMER}</p>
    </div>
  );
};
